import {
  Dag,
  TaskAction,
  buildReleaseVersion,
  generateURNByKind,
  isRunningStatus,
} from '@agilelab/plugin-wb-builder-common';
import {
  SortingState,
  TableCellProps,
  WbTable,
} from '@agilelab/plugin-wb-platform';
import { FC, useCallback, useEffect, useState } from 'react';
import { useApi } from '@mainstage/core-plugin-api';
import { panelCatalogApiRef } from '../../../api';
import { customAlertApiRef } from '@agilelab/plugin-wb-platform';
import { useReleaseDetailPageContext } from '../context/useReleaseDetailPageContext';
import { useStepIcons } from '../hooks/useStepIcons';
import { Test } from '../../ControlPanel';
import { mapToTests } from '../../EditorPage/utils';
import { Deploy } from '../types';
import { mapToDeploys } from '../utils';
import {
  createActionColumn,
  createDateColumn,
  createHistoryProcessColumn,
  createLogColumn,
  createReleaseColumn,
  createStatusColumn,
} from '../utils/deploymentColumns';
import pLimit from 'p-limit';
import useAsyncFn from 'react-use/lib/useAsyncFn';

const ROWS_PER_PAGE_OPTIONS = [15, 25, 50] as const;
const PAGE_SIZE: number = ROWS_PER_PAGE_OPTIONS[0];

interface Props {
  currentReleaseOnly?: boolean;
  onValidationGatewayClick: (
    deploy: Deploy,
    subPlanId: string | undefined,
    test: Test | undefined,
  ) => void;
}

export const DeploymentHistoryTable: FC<Props> = ({
  currentReleaseOnly = false,
  onValidationGatewayClick,
}) => {
  const {
    release,
    environment,
    entity,
    queryParamVersion,
    deploysState,
    setSelectedDeploy,
    setSelectedStep,
  } = useReleaseDetailPageContext();
  const panelCatalogApi = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const stepIcons = useStepIcons();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);

  const offset = page * rowsPerPage;

  let versionFilter: string | undefined;
  if (currentReleaseOnly) {
    versionFilter = queryParamVersion
      ? buildReleaseVersion({ v: queryParamVersion })
      : buildReleaseVersion({ release });
  }

  const sortEntry = sorting[0];
  let ordering: 'asc' | 'desc' | undefined;
  if (sortEntry) {
    ordering = sortEntry.desc ? 'desc' : 'asc';
  }

  const [{ loading, value }, fetchHistory] = useAsyncFn(async () => {
    try {
      const result =
        await panelCatalogApi.getProvisioningPlansByEntityIdAndEnvironment(
          generateURNByKind(entity.metadata.name, entity.kind),
          environment.name,
          false,
          false,
          {
            version: versionFilter,
            operations: [TaskAction.PROVISION, TaskAction.UNPROVISION],
            offset,
            limit: rowsPerPage,
            ordering,
          },
        );
      setTotal(result.page.count);
      const deploys = mapToDeploys(result.provisioningPlans, entity);

      // For each non-running deploy in this page, pre-fetch the VALIDATION_GATEWAY test
      const subPlanIds = deploys
        .filter(d => !isRunningStatus(d.status))
        .flatMap(d => d.steps)
        .filter(s => s.action === TaskAction.VALIDATION_GATEWAY && s.subPlanId)
        .map(s => s.subPlanId!);

      const uniqueSubPlanIds = [...new Set(subPlanIds)];
      const testsMap = new Map<string, Test>();

      if (uniqueSubPlanIds.length > 0) {
        const limiter = pLimit(4);
        await Promise.all(
          uniqueSubPlanIds.map(subPlanId =>
            limiter(async () => {
              const plan = await panelCatalogApi
                .getProvisioningPlan(subPlanId)
                .catch(() => undefined);
              if (!plan) return;
              const test = mapToTests([plan])?.[0];
              if (test) testsMap.set(subPlanId, test);
            }),
          ),
        );
      }

      return { deploys, testsMap };
    } catch (error) {
      alertApi.post({ error, severity: 'error' });
      return undefined;
    }
  }, [
    release,
    environment,
    entity,
    queryParamVersion,
    offset,
    rowsPerPage,
    currentReleaseOnly,
    ordering,
  ]);

  const latestProvisioningId = deploysState.value?.[0]?.id;
  const latestProvisioningStatus = deploysState.value?.[0]?.status;

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, latestProvisioningId, latestProvisioningStatus]);

  useEffect(() => {
    setPage(0);
  }, [currentReleaseOnly, queryParamVersion]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // reset to first page and update rows per page when it changes
  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, []);

  const handleSortingChange = useCallback((updater: any) => {
    setSorting(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setPage(0);
      return next;
    });
  }, []);

  const cols: TableCellProps<Deploy>[] = [
    createReleaseColumn(),
    createDateColumn(true),
    createActionColumn('Action'),
    createStatusColumn(stepIcons),
    createHistoryProcessColumn(
      value?.testsMap ?? new Map(),
      (step: Dag, deploy: Deploy) => {
        setSelectedDeploy(deploy);
        setSelectedStep(step);
      },
      onValidationGatewayClick,
    ),
    createLogColumn(),
  ];

  return (
    <WbTable<Deploy>
      stickyHeader
      noDataLabel="No Data"
      sorting={sorting}
      onSortingChange={handleSortingChange}
      manualSorting
      components={{
        tableLoader: { loading },
        tableContent: {
          columns: cols,
          rows: value?.deploys ?? [],
        },
      }}
      pagination={{
        rowsPerPageOptions: [...ROWS_PER_PAGE_OPTIONS],
        count: total,
        limit: rowsPerPage,
        offset: offset,
        onPageChange: handlePageChange,
        onRowsPerPageChange: handleRowsPerPageChange,
      }}
    />
  );
};
